(ns arthur.ui.palette
  "The palette: a grid of the open palette's slots under the tools, and the
  palette asset it is, beside that grid.

  A GRID BESIDE THE TOOLS, as Deluxe Paint and Animator Pro put theirs — the
  indexed tools this one descends from — rather than a strip of dots across the
  top: a palette is a fixed table of slots, and a grid shows it as one. Over the
  grid sits the colour a new shape gets, big, as Photoshop's foreground chip.

  Shapes store only the selected local slot number. Palette identity is supplied
  by the symbol tree, and the colour input edits the project palette asset.
  Picking a slot says what the NEXT shape is drawn in and nothing else; picking
  CLEAR makes that a knockout. What is already drawn is recoloured in the
  inspector, whose colour row offers the same slots, clear and remap.

  The footage switch is NOT here. It is a viewing aid rather than something you
  set before you draw, so it is a section of the inspector. See `ui/params`."
  (:require [arthur.domain.channel :as channel]
            [arthur.domain.clip :as clip]
            [arthur.domain.raster :as raster]
            [arthur.footage.store :as store]
            [arthur.domain.palette :as pal]
            [arthur.domain.symbol :as symbol]
            [arthur.subs.playback :as playback]
            [arthur.events.project :as project]
            [arthur.events.ui :as ui]
            [arthur.subs.render :as render]
            [arthur.subs.ui :as sub]
            [arthur.ui.canvas :as canvas]
            [arthur.ui.menu :as menu]
            [clojure.string :as str]
            [re-frame.core :as rf]
            [reagent.core :as r]))

;; WHICH PALETTE THE GRID SHOWS
;;
;; The one the open tab's stage is in at the playhead — there is only ever one
;; — read from where the player decided it, so the grid can never disagree with
;; the picture. Another can be picked to look at or retone, and each tab keeps
;; its own pick until the active one is picked again.

(rf/reg-sub ::chosen (fn [db _] (get-in db [:ui :palette (get-in db [:ui :open])])))

(rf/reg-sub
 ::followed
 :<- [::render/frame-palette]
 :<- [::render/palette]
 ;; Mid-tween, the palette it starts from, held still for the whole tween:
 ;; the only things to do with a tween are change its ends, so the grid shows
 ;; the start's own colours rather than the mix.
 (fn [[active compiled] _]
   (let [id (if (map? active) (:from active) active)]
     (if (contains? (:palettes compiled) id) id (:default compiled)))))

(rf/reg-event-db ::solo (fn [db _] (update-in db [:ui :palette-solo?] not)))

(rf/reg-event-db
 ::select
 (fn [db [_ id followed]]
   (let [open (get-in db [:ui :open])]
     (if (= id followed)
       (update-in db [:ui :palette] dissoc open)
       (project/choose-palette db id)))))

(rf/reg-event-db ::preview (fn [db [_ p]] (assoc-in db [:ui :palette-preview] p)))

(defn- shown
  "The palette asset the grid shows, and the one the tab draws in."
  []
  (let [clip     @(rf/subscribe [::render/clip])
        chosen   @(rf/subscribe [::chosen])
        followed @(rf/subscribe [::followed])
        pid      (if (contains? (:palettes clip) chosen) chosen followed)]
    [clip pid (get (pal/palettes clip) pid) followed]))

(defn- pick!
  "Make `tone` the colour the next shape is drawn in.

  WHAT IS SELECTED IS LEFT ALONE. Picking a colour to draw with recoloured the
  selection too, so choosing a colour for the next shape repainted the last one
  — which is usually still selected, because it was just drawn. Recolouring
  something already drawn is the inspector's colour swatches."
  [tone]
  (rf/dispatch [::ui/set-tone tone]))

;; ---------------------------------------------------------------------------
;; remap: a shape that lights what is under it
;;
;; A shape coloured `[:remap {from to …}]` draws nothing of its own: whatever is
;; already under it is drawn in other slots of the same palette, as Deluxe
;; Paint's and Animator Pro's shade inks did. A flashlight is a circle of it.
;; Pick MAP from the strip and draw one; its map is set in the inspector, or by
;; dragging one swatch of the strip onto another while it is selected.

(defn- remap
  "The selected node when it is a remap shape, with its map at the frame shown:
  `{:sid :id :frame :keys :map}`, or nil."
  []
  (let [[sid id n] @(rf/subscribe [::sub/selected-node])
        frame      (:frame @(rf/subscribe [::sub/selected-local]))
        ch         (get-in n [:channels [:style :color]])
        c          (when ch (channel/value-at ch (or frame 0) nil))]
    (when (symbol/remap? c)
      {:sid sid :id id :frame frame :keys (:keys ch) :map (second c)})))

(defn- map-slot!
  "Light `from` as `to` under the selected remap shape; as itself, not at all."
  [{:keys [sid id frame map]} from to]
  (rf/dispatch [::project/set-channel sid id [:style :color] frame
                [:remap (if (= from to) (dissoc map from) (assoc map from to))]]))

(defn remap-section
  "The inspector's map for a selected remap shape: each slot as what is under
  the shape will be drawn in, the original in its corner where it is changed.
  A slot opens a choice of what to draw it as."
  []
  (r/with-let [open (r/atom nil)]
    (let [[_ _ palette] (shown)
          {:keys [sid id frame keys] m :map :as rm} (remap)
          slots (:slots palette)
          hex   #(:hex (get slots %))]
      (when rm
        [:section.section
         [:h2 "remap"]
         [:div.remap-head
          [:button.key {:class (cond (contains? keys frame) "on" keys "keyed")
                        :disabled (nil? frame)
                        :title (if keys (str (count keys) " held keys") "key the map here")
                        :on-click #(rf/dispatch [::project/toggle-key sid id [:style :color] frame])}
           "◆"]
          [:span.dim (if (seq m)
                       (str "what is under it: " (count m) " slot" (when (< 1 (count m)) "s") " changed")
                       "changes nothing yet — pick a slot")]
          [:span {:style {:flex 1}}]
          (when (seq m)
            [:button {:on-click #(rf/dispatch [::project/set-channel sid id [:style :color] frame [:remap {}]])}
             "reset"])]
         [:div.remap
          (doall
           (for [i (range (count slots))]
             ^{:key i}
             [:button {:class (str "cell" (when (contains? m i) " mapped") (when (= i @open) " on"))
                       :style {:background (hex (get m i i))}
                       :title (str "slot " i (when (contains? m i) (str " → " (m i))))
                       :on-click #(swap! open (fn [o] (when-not (= o i) i)))}
              (when (contains? m i) [:i {:style {:background (hex i)}}])]))]
         (when-let [from @open]
           [:div.remap-pick
            [:span.dim (str "under it, draw " from " as")]
            (doall
             (for [i (range (count slots))]
               ^{:key i}
               [:button {:class (str "cell" (when (= i (get m from from)) " on"))
                         :style {:background (hex i)}
                         :title (if (= i from) (str i " — as it is") (str "slot " i))
                         :on-click #(do (map-slot! rm from i) (reset! open nil))}]))])]))))

(defn- swatch [pid i {:keys [name hex]} tone rm hex-of used?]
  (let [picker-id (str "palette-picker-" pid "-" i)
        to (get (:map rm) i)]
    [:div.slot {:key i}
     [:button {:class (str "cell" (when (= i tone) " on") (when-not used? " unused"))
               :style {:background hex}
               :title (str "slot " i (when name (str " · " (clojure.core/name name)))
                           " · " hex (when-not used? " · nothing in the project draws with it")
                           (when to (str " · lit as " to " under the selected remap"))
                           " — double-click to edit"
                           (when rm ", drag onto another to remap it under the selected remap"))
               :draggable (some? rm)
               :on-drag-start #(.setData (.-dataTransfer %) "application/x-arthur-slot" (str i))
               :on-drag-over (fn [^js e] (when rm (.preventDefault e)))
               :on-drop (fn [^js e]
                          (.preventDefault e)
                          (let [from (js/parseInt (.getData (.-dataTransfer e) "application/x-arthur-slot"))]
                            (when (and rm (not (js/isNaN from))) (map-slot! rm from i))))
               :on-click #(pick! i)
               :on-double-click (fn [e]
                                  (.preventDefault e)
                                  (some-> (js/document.getElementById picker-id) (.click)))}
      (when to [:i.badge {:style {:background (hex-of to)}}])]
     [:input {:id picker-id :class "palette-picker" :type "color" :value hex
              :aria-label (str "edit palette slot " i)
              :on-change #(rf/dispatch [::project/palette-color pid i (.. % -target -value)])}]]))

(defn grid
  "The colour chip and the slots, for the tool strip."
  []
  (let [[clip pid palette followed] (shown)
        tone       @(rf/subscribe [::sub/tone])
        solo?      @(rf/subscribe [::render/palette-solo?])
        used       (conj (pal/slots-used (vals (:symbols clip))) pal/bg-slot)
        hex        (when (number? tone) (:hex (get (:slots palette) tone)))]
    [:div.palette-grid
     (when (not= pid followed)
       [:button.back {:title (str "back to " (:name (get (pal/palettes clip) followed))
                                  ", the palette the stage is in")
                      :on-click #(rf/dispatch [::select followed followed])}
        "↩ active"])
     [:div {:class (str "chip" (cond (= :clear tone) " clear" (symbol/remap? tone) " map"))
            :style (when hex {:background hex})
            :title (cond (= :clear tone) "clear: what is drawn knocks out what is under it in its symbol"
                         (symbol/remap? tone) "remap: what is drawn lights what is under it"
                         :else (str "slot " tone " · " hex))}
      [:span (cond (= :clear tone) "×" (symbol/remap? tone) "⇄" :else tone)]]
     [:div.cells
      (let [rm (remap) hex-of #(:hex (get (:slots palette) %))]
        (doall (map-indexed #(swatch pid %1 %2 tone rm hex-of (contains? used %1))
                            (:slots palette))))
      [:div.slot
       [:button {:class (str "cell clear" (when (= :clear tone) " on"))
                 :title "clear — draws a knockout: a hole in its symbol"
                 :on-click #(pick! :clear)}]]
      [:div.slot
       [:button {:class (str "cell map" (when (symbol/remap? tone) " on"))
                 :title "remap — draws light: what is under it is drawn in other slots of the palette. Set which in the inspector."
                 :on-click #(pick! [:remap {}])}
        "⇄"]]]
     [:div.grid-tools
      [:button {:class (when solo? "on")
                :disabled (not (number? tone))
                :title (if solo?
                         "showing only what is drawn in this colour — click one to select it; click again to show everything"
                         "show only what is drawn in the chosen colour, to find it and recolour it in the inspector")
                :aria-pressed (boolean solo?)
                :on-click #(rf/dispatch [::solo])}
       "◎"]
      [:button {:title (str "empty the colours of " (:name palette)
                            " no shape in the project draws with — they stay as free slots."
                            " The background and the named face tones are kept.")
                :aria-label "empty unused colours"
                :on-click #(rf/dispatch [::project/empty-unused-slots pid])}
       "⌫"]]]))

(defn strip
  "Every slot of a palette, side by side: what it looks like at a glance."
  [{:keys [slots]}]
  [:span.strip (for [[i {:keys [hex]}] (map-indexed vector slots)]
                 ^{:key i} [:i {:style {:background hex}}])])

(defn- rename
  "The open palette's name, made editable. Committed on Enter or blur, so a
  rename is one undo step and not one per letter; Esc leaves it as it was."
  [pid palette done!]
  [:input.palette-name
   {:type "text" :default-value (:name palette) :auto-focus true
    :aria-label "palette name"
    :on-focus #(.select (.-target %))
    :on-blur (fn [^js e]
               (rf/dispatch [::project/palette-name pid (.. e -target -value)])
               (done!))
    :on-key-down (fn [^js e]
                   (case (.-key e)
                     "Enter" (.blur (.-target e))
                     "Escape" (do (set! (.. e -target -value) (:name palette))
                                  (.blur (.-target e)))
                     nil))}])

(defn- authored?
  "Not exactly one of the shelf's: a palette someone made or retoned."
  [hexes]
  (not-any? #(= hexes (pal/hexes-of %)) pal/presets))

(defonce ^:private thumbs (atom {:key nil :raster nil :urls {}}))

(defn- frame-raster
  "The open symbol's frame at the playhead, as indices: drawn once, then shown
  through each palette's colours, so a list of thumbnails costs one render and a
  colour lookup per row."
  [clip store compiled open frame]
  (let [k [clip open frame]]
    (when-not (= k (:key @thumbs))
      (reset! thumbs
              {:key k :urls {}
               :raster (try
                         (let [resolve (clip/resolver clip open store compiled {})
                               [w h]   (clip/stage clip open)
                               ops     (resolve (or frame 0))]
                           (-> (raster/make w h)
                               (raster/clear! pal/bg-slot)
                               (raster/draw-ops! ops)))
                         (catch :default _ nil))}))
    (:raster @thumbs)))

(defn- thumb
  "The playhead's frame in `hexes`."
  [hexes]
  (let [k hexes]
    (or (get-in @thumbs [:urls k])
        (when-let [r (:raster @thumbs)]
          (let [url (canvas/->png r (pal/ramp-of hexes))]
            (swap! thumbs assoc-in [:urls k] url)
            url)))))

(defn- hit? [needle & texts]
  (or (str/blank? needle)
      (some #(str/includes? (str/lower-case (str %)) needle) texts)))

(def ^:private via-words {:symbol "set on symbol" :lane "palette lane" :instance "instance"})

(defn- candidate-row
  "One palette to switch to: a thumbnail of the playhead's frame in it, its strip,
  its name and where it is from. Hovering shows it on the stage; clicking opens
  it in the grid; `use` makes the open symbol draw in it."
  [{:keys [p note on? thumb-url sid followed]}]
  (let [hexes (pal/hexes-of p)]
    [:div.palette-row {:class (when on? "on")
                       :on-mouse-enter #(rf/dispatch [::preview {:hexes hexes}])
                       :on-mouse-leave #(rf/dispatch [::preview nil])}
     [:button.choose {:role "option" :aria-selected (boolean on?)
                      :title (str "show " (:name p) " in the palette grid")
                      :on-click (fn []
                                  (rf/dispatch [::preview nil])
                                  (if (contains? (pal/palettes @(rf/subscribe [::render/clip])) (:id p))
                                    (rf/dispatch [::select (:id p) followed])
                                    (rf/dispatch [::project/add-palette p])))}
      (when thumb-url [:img.thumb {:src thumb-url :alt ""}])
      [:span.text
       [strip p]
       [:span.name (:name p)]
       (when note [:span.note note])]]
     [:button.use {:title (str "draw " (clip/symbol-name @(rf/subscribe [::render/clip]) sid)
                               " in " (:name p))
                   :on-click (fn []
                               (rf/dispatch [::preview nil])
                               (rf/dispatch [::project/use-palette sid p]))}
      "use"]]))

(defn- shelf
  "The classics, small: a strip each and a name under it, folded away until
  wanted. Hovering previews; a click brings one into the project."
  [ps followed searching? sid]
  [:details.palette-shelf {:open searching?}
   [:summary (str "classics · " (count ps))]
   [:div.shelf
    (doall
     (for [p ps]
       ^{:key (str (:id p))}
       [:button.tile {:title (str (:name p) " — click to open it, double-click to use it")
                      :on-mouse-enter #(rf/dispatch [::preview {:hexes (pal/hexes-of p)}])
                      :on-mouse-leave #(rf/dispatch [::preview nil])
                      :on-click (fn []
                                  (rf/dispatch [::preview nil])
                                  (if (contains? (pal/palettes @(rf/subscribe [::render/clip])) (:id p))
                                    (rf/dispatch [::select (:id p) followed])
                                    (rf/dispatch [::project/add-palette p])))
                      :on-double-click #(rf/dispatch [::project/use-palette sid p])}
        [strip p] [:span.name (:name p)]]))]])

(defn- chooser
  "Every palette worth switching to, most relevant first: those the open
  symbol's tree draws in, saying where; the rest of this project's; those made
  in other projects, with the project's name; then the classics, folded. One
  search reaches palette names and project names."
  [clip pid followed]
  (r/with-let [renaming? (r/atom false) query (r/atom "")]
    (let [sid       @(rf/subscribe [::render/open])
          compiled  @(rf/subscribe [::render/palette])
          frame     @(rf/subscribe [::render/open-frame])
          clip-id   @(rf/subscribe [::render/clip-id])
          listed    (:palettes @(rf/subscribe [::project/assets]))
          {project-id :id} @(rf/subscribe [::playback/project])
          needle    (str/lower-case (str/trim @query))
          searching? (not (str/blank? needle))
          _         (frame-raster clip (:store (store/entry clip-id)) compiled sid frame)
          own       (pal/palettes clip)
          uses      (group-by :palette
                              (cons {:palette (pal/default-palette-id clip) :project? true}
                                    (clip/palette-uses clip sid)))
          note-of   (fn [id]
                      (str/join ", " (for [{:keys [sid via project?]} (get uses id)]
                                       (if project? "project"
                                           (str (clip/symbol-name clip sid) " · " (via-words via))))))
          row       (fn [p note]
                      {:p p :note note :on? (= (:id p) pid) :sid sid :followed followed
                       :thumb-url (thumb (pal/hexes-of p))})
          here      (for [[id p] own :when (contains? uses id)] (row p (note-of id)))
          project   (for [[id p] own
                          :when (and (not (contains? uses id)) (authored? (pal/hexes-of p)))]
                      (row p nil))
          have      (set (map pal/hexes-of (vals own)))
          elsewhere (->> listed
                         (remove #(= project-id (:project %)))
                         (filter #(and (seq (:hexes %)) (authored? (:hexes %))
                                       (not (contains? have (:hexes %)))))
                         (group-by (juxt :name :hexes))
                         (map (fn [[[label hexes] rows]]
                                (let [projects (str/join ", " (distinct (map :project-name rows)))]
                                  (assoc (row (pal/from-hexes (random-uuid) label hexes) projects)
                                         :key [label hexes] :search projects))))
                         (sort-by (comp str/lower-case :name :p)))
          keep      (fn [rows] (filter #(hit? needle (:name (:p %)) (:note %)) rows))
          section   (fn [title rows]
                      (when (seq rows)
                        [:<>
                         [:div.palette-head title]
                         (doall (for [r rows] ^{:key (str (or (:key r) (:id (:p r))))} [candidate-row r]))]))]
      [:div.palette-chooser
       [:input.palette-search {:type "search" :placeholder "search palettes and projects"
                               :aria-label "search palettes"
                               :value @query :on-change #(reset! query (.. % -target -value))}]
       [:div.palette-list {:role "listbox" :aria-label "palettes"}
        [section (str "in " (clip/symbol-name clip sid)) (keep here)]
        [section "this project" (keep project)]
        [section "your palettes" (keep elsewhere)]]
       (when-let [open-p (and (not searching?) (get own pid))]
         [:div.palette-editing
          (if @renaming?
            [rename pid open-p #(reset! renaming? false)]
            [:<> [:span.dim "editing"] [:span.name (:name open-p)]
             [:button.rename {:title (str "rename " (:name open-p)) :aria-label "rename palette"
                              :on-click #(swap! renaming? not)} "✎"]])])
       [shelf (filter #(hit? needle (:name %)) pal/presets) followed searching? sid]])))

(defn assets
  "The palettes behind a button at the foot of the strip: switch, rename, add.
  The name is NOT printed in the strip: 44px holds no palette's name, and a name
  broken over three lines is worse than one shown when asked for."
  []
  (let [[clip pid palette followed] (shown)]
    [:div.palette-assets-wrap
     [menu/popover {:class "palette-assets" :title (str "palette: " (:name palette))
                    :label [:svg {:view-box "0 0 18 18" :width 18 :height 18}
                            [:path {:d "M9 2C4.6 2 1.5 5.2 1.5 9c0 4 3.4 7 7.3 7 1.4 0 1.9-.9 1.4-1.9-.5-1 0-2.1 1.4-2.1h1.9c1.9 0 3-1.2 3-3.2C16.5 5 13.2 2 9 2Z"}]
                            [:circle {:cx 5.5 :cy 8 :r 1.3}]
                            [:circle {:cx 8.5 :cy 5 :r 1.3}]
                            [:circle {:cx 12.5 :cy 6.5 :r 1.3}]]}
      (fn [_]
        [:div.palette-popout
         [chooser clip pid followed]
         [:div.row
          [:button {:title "a new 16-slot palette" :on-click #(rf/dispatch [::project/new-palette])} "new"]
          [:button {:title (str "a copy of " (:name palette) " you can retone")
                    :on-click #(rf/dispatch [::project/duplicate-palette pid])} "duplicate"]]])]]))
